import type { SubmitHandler } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import type { InferType } from 'yup';
import type { tikTokSchema } from '../validation/tiktok.schema';
import { memo, Suspense, useCallback, useEffect, useMemo } from 'react';
import { useCreateTikTokAd } from '../api/createTikTokAd';
import {
  AddPackButton,
  AdCreationProgress,
  CircularProgress,
  ConfirmationDialog,
} from 'components/common';
import useJobStatusCheck from 'hooks/useJobStatusCheck';
import { JobResultTable } from './TikTokFormComponents';
import { FormNavigation, StepperPaper } from 'components/layout';
import useStepper from 'hooks/useStepper';
import ChooseCampaign from './ChooseCampaign';
import ChooseCreative from './ChooseCreative';
import ChooseAdSet from './ChooseAdSet';
import AdSettings from './AdSettings';
import { useEnqueueSnackbar } from 'hooks';
import AppAccount from 'features/tiktok/components/AppAccount';
import useCreateTikTokAdParams from '../hooks/useCreateTikTokAdParams';

export type TikTokFormValues = InferType<typeof tikTokSchema>;

const TikTokForm = memo(() => {
  const { onShowErrorAlert } = useEnqueueSnackbar();

  const {
    trigger,
    reset: resetForm,
    handleSubmit,
    setValue,
    formState: { isValid },
  } = useFormContext<TikTokFormValues>();

  const initialSteps = useMemo(
    () => [
      { title: 'App & account', component: AppAccount },
      { title: 'Creatives', component: ChooseCreative },
      { title: 'Campaign', component: ChooseCampaign },
      { title: 'Ad sets', component: ChooseAdSet },
      { title: 'Ad settings', component: AdSettings },
    ],
    []
  );

  const { currentStep, resetSteps, prevStep, nextStep, StepComponent } =
    useStepper({ initialSteps, defaultStep: 0 });

  const {
    data: jobStatusResponse,
    runJubStatusCheck,
    isLoading: isJobStatusChecking,
    isFinished: isJobStatusFinished,
    clearJobStatus,
  } = useJobStatusCheck();

  const createTikTokAd = useCreateTikTokAd({
    config: {
      onSuccess: (r) => {
        runJubStatusCheck(r.job_id as string);
      },
      onError: () => {
        onShowErrorAlert('Internal Error');
      },
    },
  });

  const { params: createTikTokAdParams } = useCreateTikTokAdParams();

  useEffect(() => {
    setValue('currentStep', currentStep, { shouldValidate: true });
  }, [currentStep, setValue]);

  const onSubmit = useCallback<SubmitHandler<TikTokFormValues>>(() => {
    createTikTokAd.mutate(createTikTokAdParams);
  }, [createTikTokAdParams, createTikTokAd]);

  const handleNextStep = useCallback(() => {
    void trigger();
    if (!isValid) return;

    if (currentStep === initialSteps.length - 1) {
      void handleSubmit(onSubmit)();
    } else {
      nextStep();
    }
  }, [
    nextStep,
    trigger,
    handleSubmit,
    onSubmit,
    initialSteps,
    currentStep,
    isValid,
  ]);

  const handlePrevStep = useCallback(() => {
    prevStep();
  }, [prevStep]);

  const handleRestart = useCallback(() => {
    resetForm();
    resetSteps();
    clearJobStatus();
  }, [resetForm, resetSteps, clearJobStatus]);

  const handleLaunchMoreAds = useCallback(() => {
    window.open('/tiktok', '_blank', 'noopener');
  }, []);

  const isDisabledNext =
    !isValid || createTikTokAd.isPending || !!isJobStatusFinished;

  return (
    <StepperPaper
      title={'TikTok Ads Automation'}
      steps={initialSteps}
      activeStep={currentStep}
    >
      <>
        {isJobStatusChecking ? (
          <AdCreationProgress onLaunchMoreAds={handleLaunchMoreAds} />
        ) : (
          <Suspense fallback={<CircularProgress />}>
            {isJobStatusFinished && jobStatusResponse ? (
              <JobResultTable rows={jobStatusResponse.result} />
            ) : (
              <StepComponent />
            )}
          </Suspense>
        )}
        <FormNavigation
          isJobStatusFinished={isJobStatusFinished}
          onRestart={handleRestart}
          isDisabledRestart={!currentStep && !isJobStatusFinished}
          onBack={handlePrevStep}
          isDisabledBack={
            !currentStep || createTikTokAd.isPending || !!isJobStatusFinished || !!isJobStatusChecking
          }
          onNext={handleNextStep}
          isDisabledNext={isDisabledNext ?? !!isJobStatusChecking}
          titleNext={
            currentStep === initialSteps.length - 1 ? 'Create Ad' : 'Next'
          }
          childrenOnTheRightSide={
            <>{currentStep === 1 ? <AddPackButton /> : null}</>
          }
        />
      </>
      {!jobStatusResponse?.result && (
        <ConfirmationDialog
          isUnblock={createTikTokAd.isSuccess && isJobStatusFinished}
        />
      )}
    </StepperPaper>
  );
});

TikTokForm.displayName = 'TikTokForm';

export default TikTokForm;
